import { DateOnly } from '@if/core/date'
import { formatCurrency, formatIsoDate } from '@if/core/utils'
import {
  ControlledDebouncedNumberField,
  ControlledSelect,
  ErrorAlert,
  getErrorMessage,
  useDollarAdornment,
} from '@if/mui'
import { Alert, Box, MenuItem, Stack, Typography } from '@mui/material'
import { useCalculateNumberOfPaymentsToClearOverdueAmount } from '@src/api/loan/loan-api'
import FormDialog from '@src/components/Common/Dialog/FormDialog'
import { useUser } from '@src/providers/UserProvider'
import { useUnmountableDialog } from '@src/services/dialog'
import { EOperationStatus, EPaymentMethod } from '@src/types/constants'
import { CustomerPaymentOperation } from '@src/types/loan/loan-operation'
import { SignedCalendarSimulation } from '@src/types/loan/loan-simulation'
import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useGetCurrentOverdueAmount } from '../../LoanCalendar-hooks'
import EditPaymentCloseAllBtn from '../EditPaymentCloseAllBtn'
import { EditPaymentSchemaType } from '../EditPayments-schema'
import { BatchUpdatePaymentsSchemaType, useBatchUpdatePaymentsSchema } from './LoanBatchUpdatePayments-schema'

interface LoanBatchUpdatePaymentsDialogProps {
  simulation: SignedCalendarSimulation
  onConfirm: (amount: number, fromPaymentDate?: DateOnly | null) => void
  onCloseAll: () => void
  onClose: () => void
  isCatchUpMode?: boolean
  isIncreasingPayments?: boolean
  payments?: EditPaymentSchemaType[]
  onLoadPayments?: (fromDate: DateOnly, numberOfPayments: number, amount: number, lastPaymentAmount: number) => void
}

const LoanBatchUpdatePaymentsDialog = ({
  simulation,
  onConfirm,
  onCloseAll,
  onClose,
  isCatchUpMode = false,
  isIncreasingPayments = true,
  payments = [],
  onLoadPayments,
}: LoanBatchUpdatePaymentsDialogProps) => {
  const { t } = useTranslation()
  const user = useUser()
  const canBypassAcpLock = user?.rights.canEditLockedPpaPayments ?? false
  const { resolver, defaultValues } = useBatchUpdatePaymentsSchema(undefined)
  const { control, handleSubmit, watch } = useForm<BatchUpdatePaymentsSchemaType>({
    resolver,
    defaultValues,
  })
  const selectedPaymentDate = watch('fromPaymentDate')
  const amount = watch('amount')
  const currentOverdueAmount = useGetCurrentOverdueAmount(simulation.simulation)

  const {
    data: paymentCalculationResult,
    isFetching: isCalculatingPayments,
    error: paymentCalculationError,
  } = useCalculateNumberOfPaymentsToClearOverdueAmount(
    simulation.simulation.fullLoan.loan.id,
    simulation.simulation.fullLoan.loan.financingProgramId,
    simulation,
    selectedPaymentDate ?? DateOnly.today(),
    amount ?? 0,
    isCatchUpMode && !!selectedPaymentDate && !!amount && amount > 0,
  )

  const isFetching = isCalculatingPayments
  const errorApi = paymentCalculationError

  const { closeDialog, dialogProps } = useUnmountableDialog(onClose)
  const dollarAdornment = useDollarAdornment()

  const paymentDateOptions = useMemo(() => {
    if (isCatchUpMode) {
      const nextValidAction = simulation.simulation.getNextValidActionDate(
        EPaymentMethod.PreAuthorizedPayment,
        canBypassAcpLock,
      )
      const customerPayments = simulation.simulation.operationInformations
        .filter(
          (opInfo) =>
            opInfo.isCustomerPaymentOperation() &&
            opInfo.operation.operationStatusId === EOperationStatus.Planned &&
            opInfo.operation.effectiveDate.getTime() >= nextValidAction.getTime(),
        )
        .map((opInfo) => opInfo.operation as CustomerPaymentOperation)
        .sort((a, b) => a.effectiveDate.getTime() - b.effectiveDate.getTime())

      return customerPayments.map((payment) => (
        <MenuItem key={payment.id} value={formatIsoDate(payment.effectiveDate)}>
          {`${formatIsoDate(payment.effectiveDate)} - ${formatCurrency(payment.amount)}`}
        </MenuItem>
      ))
    } else {
      return payments
        .filter((p) => !p.isDeleted && !p.isLocked)
        .map((payment) => (
          <MenuItem key={payment.id} value={formatIsoDate(payment.effectiveDate)}>
            {`${formatIsoDate(payment.effectiveDate)} - ${formatCurrency(payment.amount)}`}
          </MenuItem>
        ))
    }
  }, [isCatchUpMode, simulation, payments, canBypassAcpLock])

  const handleConfirm = handleSubmit((data) => {
    if (isCatchUpMode && onLoadPayments && data.fromPaymentDate && paymentCalculationResult) {
      onLoadPayments(
        data.fromPaymentDate,
        paymentCalculationResult.numberOfPayments,
        data.amount,
        paymentCalculationResult.lastPaymentAmount,
      )
      closeDialog()
    } else {
      if (isIncreasingPayments) onConfirm(data.amount, data.fromPaymentDate)
      else onConfirm(-data.amount, data.fromPaymentDate)
      closeDialog()
    }
  })

  const getMessageComplement = useMemo(() => {
    if (isCatchUpMode || isIncreasingPayments) {
      return t('loan.payment.batchUpdatePayments.increase')
    }
    return t('loan.payment.batchUpdatePayments.decrease')
  }, [isCatchUpMode, isIncreasingPayments, t])

  const isConfirmDisabled = !amount || (isCatchUpMode && !selectedPaymentDate) || errorApi != null
  const error = useMemo(() => {
    if (!isFetching && isCatchUpMode && currentOverdueAmount <= 0)
      return t('loan.payment.batchUpdatePayments.errors.noOverdueAmount')

    // TODO: Replace with translated business error from API
    const formattedError = getErrorMessage(errorApi)
    if (formattedError?.toLowerCase().includes('unable to clear overdue amount')) {
      return t('loan.payment.batchUpdatePayments.errors.unableToClearOverdue')
    }

    return errorApi
  }, [isFetching, isCatchUpMode, currentOverdueAmount, t, errorApi])

  return (
    <FormDialog
      {...dialogProps}
      title={t('loan.payment.batchUpdatePayments.title')}
      onConfirm={handleConfirm}
      disabled={isConfirmDisabled}
      additionalActions={<EditPaymentCloseAllBtn onCloseAll={onCloseAll} />}
    >
      {error && <ErrorAlert error={error} />}
      <Stack spacing={2}>
        {isCatchUpMode && (
          <>
            <Box
              sx={{
                textAlign: 'center',
                bgcolor: 'action.hover',
                p: 1.5,
                borderRadius: 1,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {t('loan.payment.batchUpdatePayments.overdueAmount')}
              </Typography>
              <Typography variant="h4" color="primary" fontWeight="bold">
                {formatCurrency(currentOverdueAmount)}
              </Typography>
            </Box>
            <ControlledSelect
              control={control}
              name="fromPaymentDate"
              options={paymentDateOptions}
              parseValue={(v) => (v ? new DateOnly(v) : null)}
              label={t('loan.payment.batchUpdatePayments.fromPaymentDate')}
              disableEmptyOption
            />
          </>
        )}
        <ControlledDebouncedNumberField
          control={control}
          name="amount"
          slotProps={{ input: dollarAdornment }}
          label={t('loan.payment.batchUpdatePayments.amountLabel', { op: getMessageComplement })}
          autoFocus={!isCatchUpMode}
          debounceTime={350}
        />
        {isCatchUpMode && !isFetching && paymentCalculationResult && (
          <Alert severity="info">
            {t('loan.payment.batchUpdatePayments.catchUpInfo', {
              count: paymentCalculationResult.numberOfPayments,
              amount: formatCurrency(paymentCalculationResult.lastPaymentAmount),
            })}
          </Alert>
        )}
      </Stack>
    </FormDialog>
  )
}

export default LoanBatchUpdatePaymentsDialog
